/**
 * useAircraftSchedules — TanStack Query hooks for aircraft_schedules
 *
 * Movement ledger model: directional records (outbound/inbound) with pair_id linking.
 * Schedules are aircraft-level; allocation relationship derived via temporal join.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchAircraftSchedules,
  fetchUpcomingAircraftSchedules,
  createAircraftSchedule,
  updateAircraftSchedule,
} from '@/lib/api/aircraft-schedules'
import { supabase } from '@/lib/supabase'
import { broadcastInvalidation } from '@/lib/sync'
import type { AircraftScheduleFormData } from '@/types/aircraft-schedules'

// ============================================
// Query Key Factory
// ============================================

export const aircraftScheduleKeys = {
  all: ['aircraft-schedules'] as const,
  byAircraft: (id: string) => ['aircraft-schedules', 'aircraft', id] as const,
  upcoming: (id: string) => ['aircraft-schedules', 'upcoming', id] as const,
}

// ============================================
// Queries
// ============================================

/**
 * Full schedule list for an aircraft (used in ScheduleTab)
 */
export function useAircraftSchedules(aircraftId: string | null) {
  return useQuery({
    queryKey: aircraftScheduleKeys.byAircraft(aircraftId ?? ''),
    queryFn: () => fetchAircraftSchedules(aircraftId!),
    enabled: !!aircraftId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Upcoming schedules for an aircraft (future departures + returns)
 */
export function useUpcomingAircraftSchedules(aircraftId: string | null) {
  return useQuery({
    queryKey: aircraftScheduleKeys.upcoming(aircraftId ?? ''),
    queryFn: () => fetchUpcomingAircraftSchedules(aircraftId!),
    enabled: !!aircraftId,
    staleTime: 60_000,
  })
}

// ============================================
// Mutations
// ============================================

/**
 * Create a new aircraft schedule
 */
export function useCreateAircraftSchedule() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: AircraftScheduleFormData) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return createAircraftSchedule(currentOrganization.id, data)
    },
    onSuccess: (_result, variables) => {
      const keys = [
        aircraftScheduleKeys.byAircraft(variables.aircraft_id),
        ['aircraft-schedules', 'upcoming'],
        ['flight-line-schedules'],
        ['map-allocations'],
        ['location-schedules'],
      ] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
    },
  })
}

/**
 * Update an aircraft schedule
 */
export function useUpdateAircraftSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<AircraftScheduleFormData> & {
      event_id?: string
      actual_at?: string
      pair_id?: string
    }) => updateAircraftSchedule(id, data),
    onSuccess: () => {
      const keys = [['aircraft-schedules'], ['flight-line-schedules'], ['map-allocations'], ['location-schedules']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
    },
  })
}

/**
 * Cancel an aircraft schedule (sets status to 'cancelled').
 * When cancelling an outbound, also cancel any paired inbound.
 */
export function useCancelAircraftSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // Cancel the target schedule
      const result = await updateAircraftSchedule(id, { status: 'cancelled' })

      // If cancelling an outbound, also cancel any paired inbound
      if (result.direction === 'outbound') {
        const { data: pairedInbound } = await supabase
          .from('aircraft_schedules')
          .select('id')
          .eq('pair_id', id)
          .in('status', ['scheduled', 'confirmed'])

        if (pairedInbound && pairedInbound.length > 0) {
          for (const sched of pairedInbound) {
            await updateAircraftSchedule(sched.id, { status: 'cancelled' })
          }
        }
      }

      return result
    },
    onSuccess: () => {
      const keys = [['aircraft-schedules'], ['flight-line-schedules'], ['map-allocations'], ['location-schedules']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
    },
  })
}
